import _defineProperty from '@babel/runtime/helpers/defineProperty';
import * as React from 'react';
import { accordionClasses, paperClasses, typographyClasses, accordionSummaryClasses, buttonBaseClasses, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Icon } from '../Icon/Icon.js';
import { IconButton } from '../IconButton/IconButton.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { Typography } from '../Typography/Typography.js';
import { theme } from '../../utils/themeProvider.js';
import '@mui/styles';
import '@mui/material/styles';
import keygen from 'uuid/v4';
import { createButton } from '../../factories/button/button.factory.js';
import { LayoutGrid } from '../LayoutGrid/LayoutGrid.js';

var _root, _content;

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

var styles = {
  root: (_root = {
    marginTop: 0,
    marginBottom: 2
  }, _defineProperty(_root, "&.".concat(accordionClasses['root']), {}), _defineProperty(_root, "&.".concat(accordionClasses['expanded']), {
    marginTop: 0,
    marginBottom: 2,
    minHeight: '1.875rem'
  }), _defineProperty(_root, "&.".concat(paperClasses['elevation']), {
    boxShadow: 'none'
  }), _defineProperty(_root, "& .".concat(typographyClasses['root']), {
    marginRight: '6px'
  }), _defineProperty(_root, "& .".concat(accordionSummaryClasses['root']), {
    minHeight: '5px'
  }), _defineProperty(_root, "& .".concat(buttonBaseClasses['root']), {
    alignItems: 'center',
    marginTop: '2px'
  }), _root),
  infoClass: {
    order: 2
  },
  customActionsWrapper: _defineProperty({
    order: 2,
    display: 'flex',
    marginLeft: 1,
    '& svg': {
      fill: 'common.white'
    }
  }, "& .".concat(buttonBaseClasses['root']), {
    '&:hover': {
      background: 'rgba(255,255,255,0.07)'
    },
    '&:active, &:focus': {
      background: 'rgba(255,255,255,0.15)'
    }
  }),
  content: (_content = {
    backgroundColor: 'text.secondary',
    color: 'common.white',
    paddingLeft: 0,
    height: 'auto'
  }, _defineProperty(_content, "& .".concat(accordionSummaryClasses['content'], ".").concat(accordionSummaryClasses['contentGutters']), {
    alignItems: 'center',
    margin: '4px 0'
  }), _defineProperty(_content, "textTransform", theme.typography.button.textTransform), _defineProperty(_content, '& :first-child', {
    order: 2,
    alignItems: 'flex-start'
  }), _defineProperty(_content, "&.".concat(accordionClasses['expanded']), {
    minHeight: '1.875rem'
  }), _defineProperty(_content, "&.".concat(accordionSummaryClasses['focusVisible']), {
    backgroundColor: 'rgba(97, 97, 97, 0.75)'
  }), _defineProperty(_content, '& [class*="MuiIconButton"]', {
    fontSize: '1.25rem',
    margin: 0,
    padding: 0,
    color: 'common.white'
  }), _defineProperty(_content, '& [icon*="InfoOutlined"]', {
    margin: '0 0.5rem'
  }), _defineProperty(_content, '& [class*="expandIcon"]', _defineProperty({
    order: 1,
    color: 'common.white',
    left: 0,
    right: 'auto',
    paddingLeft: 0,
    '& :last-child': {
      padding: 0
    },
    '&[class*="MuiButtonBase"]': {
      padding: 0
    },
    transform: 'rotate(-90deg)'
  }, "&.".concat(accordionClasses['expanded']), {
    transform: 'rotate(0deg)'
  })), _content),
  basicContent: {
    color: 'text.primary',
    height: 'auto',
    '& [class*="expandIcon"]': {
      color: 'text.primary'
    }
  }
};

var handleChange = function handleChange(handler) {
  return function () {
    if (handler) {
      handler();
    }
  };
};

var ExpansionPanel = function ExpansionPanel(_ref) {
  var children = _ref.children,
      dataProp = _ref.dataProp,
      title = _ref.title,
      infoTooltipText = _ref.infoTooltipText,
      defaultExpanded = _ref.defaultExpanded,
      expanded = _ref.expanded,
      disabled = _ref.disabled,
      changeHandler = _ref.changeHandler,
      square = _ref.square,
      headerActions = _ref.headerActions,
      showBasicExpansionPanel = _ref.showBasicExpansionPanel,
      sx = _ref.sx;
  var basicContent = styles.basicContent,
      content = styles.content,
      root = styles.root,
      infoClass = styles.infoClass,
      customActionsWrapper = styles.customActionsWrapper;
  var expansionPanelId = keygen();
  return /*#__PURE__*/React.createElement(Accordion, {
    sx: [root, sx],
    defaultExpanded: defaultExpanded,
    expanded: expanded,
    disabled: disabled,
    onChange: handleChange(changeHandler),
    square: square,
    "data-selector": dataProp
  }, /*#__PURE__*/React.createElement(AccordionSummary, {
    sx: showBasicExpansionPanel ? basicContent : content,
    expandIcon: showBasicExpansionPanel ? /*#__PURE__*/React.createElement(Icon, {
      icon: "ExpandMore"
    }) : /*#__PURE__*/React.createElement(Icon, {
      icon: "ArrowDropDown"
    }),
    id: "Expansion-Panel-Header-".concat(expansionPanelId),
    "aria-controls": "Expansion-Panel-Content-".concat(expansionPanelId)
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, title), infoTooltipText && !headerActions ? /*#__PURE__*/React.createElement(Tooltip, {
    "aria-label": infoTooltipText,
    sx: infoClass,
    title: infoTooltipText
  }, /*#__PURE__*/React.createElement(IconButton, {
    icon: "InfoOutlined",
    fontSize: "small",
    isExpansionPanelIcon: true,
    actionType: "INFO"
  })) : null, headerActions ? /*#__PURE__*/React.createElement(LayoutGrid, {
    sx: customActionsWrapper
  }, headerActions === null || headerActions === void 0 ? void 0 : headerActions.map(function (action) {
    return createButton(_objectSpread(_objectSpread({}, action), {}, {
      onClick: function onClick(e) {
        e.stopPropagation();
        action.onClick && action.onClick(e);
      }
    }));
  })) : null), children && /*#__PURE__*/React.createElement(AccordionDetails, null, children));
};

ExpansionPanel.defaultProps = {
  defaultExpanded: true,
  disabled: false,
  square: true,
  title: ''
};
ExpansionPanel.displayName = 'ExpansionPanel';
export { ExpansionPanel };